import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle token refresh on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          const response = await axios.post(`${API_URL}/auth/refresh`, {}, {
            headers: { Authorization: `Bearer ${refreshToken}` },
          });

          const { access_token } = response.data;
          localStorage.setItem('access_token', access_token);

          originalRequest.headers.Authorization = `Bearer ${access_token}`;
          return api(originalRequest);
        } catch (refreshError) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/login';
        }
      }
    }

    return Promise.reject(error);
  }
);

// Auth
export const authAPI = {
  register: (data: { email: string; password: string; name: string }) =>
    api.post('/auth/register', data),
  login: (data: { email: string; password: string }) =>
    api.post('/auth/login', data),
  googleLogin: (credential: string) =>
    api.post('/auth/google', { credential }),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
};

// Dashboard
export const dashboardAPI = {
  get: () => api.get('/dashboard'),
};

// Accounts
export const accountsAPI = {
  getAll: () => api.get('/accounts'),
  create: (data: any) => api.post('/accounts', data),
  update: (id: number, data: any) => api.put(`/accounts/${id}`, data),
  delete: (id: number) => api.delete(`/accounts/${id}`),
  getBalanceHistory: (id: number) => api.get(`/accounts/${id}/balance-history`),
  createBalanceSnapshot: (id: number, data: any) => api.post(`/accounts/${id}/balance-snapshot`, data),
  deleteBalanceSnapshot: (id: number) => api.delete(`/balance-snapshots/${id}`),
};

// Assets
export const assetsAPI = {
  getAll: () => api.get('/assets'),
  create: (data: any) => api.post('/assets', data),
  update: (id: number, data: any) => api.put(`/assets/${id}`, data),
  delete: (id: number) => api.delete(`/assets/${id}`),
};

// Liabilities
export const liabilitiesAPI = {
  getAll: () => api.get('/liabilities'),
  create: (data: any) => api.post('/liabilities', data),
  update: (id: number, data: any) => api.put(`/liabilities/${id}`, data),
  delete: (id: number) => api.delete(`/liabilities/${id}`),
};

// Budget
export const budgetAPI = {
  getAll: () => api.get('/budget'),
  create: (data: any) => api.post('/budget', data),
  update: (id: number, data: any) => api.put(`/budget/${id}`, data),
  delete: (id: number) => api.delete(`/budget/${id}`),
  aiCategorize: (items: string[]) => api.post('/budget/categorize', { items }),
};

// Categories
export const categoriesAPI = {
  getAll: () => api.get('/categories'),
  create: (data: any) => api.post('/categories', data),
  update: (id: number, data: any) => api.put(`/categories/${id}`, data),
  delete: (id: number) => api.delete(`/categories/${id}`),
  seedDefaults: () => api.post('/categories/seed-defaults'),
};

// Transactions
export const transactionsAPI = {
  getAll: (params?: any) => api.get('/transactions', { params }),
  create: (data: any) => api.post('/transactions', data),
  update: (id: number, data: any) => api.put(`/transactions/${id}`, data),
  delete: (id: number) => api.delete(`/transactions/${id}`),
  split: (id: number, splits: any[]) => api.post(`/transactions/${id}/split`, { splits }),

  // Soft delete
  softDelete: (options: { transaction_ids?: number[]; start_date?: string; end_date?: string; account_id?: number }) =>
    api.post('/transactions/soft-delete', options),
  getDeleted: () => api.get('/transactions/deleted'),
  restore: (transactionIds: number[]) => api.post('/transactions/restore', { transaction_ids: transactionIds }),

  // Duplicates
  getDuplicates: () => api.get('/transactions/duplicates'),
  deleteDuplicate: (id: number) => api.post(`/transactions/duplicates/${id}/delete`),
  deleteAllDuplicates: () => api.post('/transactions/duplicates/delete-all'),
  markNotDuplicate: (id: number) => api.post(`/transactions/duplicates/${id}/not-duplicate`),

  // AI Categorization
  aiCategorize: (transactionIds: number[]) => api.post('/transactions/categorize', { transaction_ids: transactionIds }),
};

// Tags
export const tagsAPI = {
  getAll: () => api.get('/tags'),
  create: (data: any) => api.post('/tags', data),
  delete: (id: number) => api.delete(`/tags/${id}`),
};

// Categorization Rules
export const rulesAPI = {
  getAll: () => api.get('/rules'),
  create: (data: any) => api.post('/rules', data),
  delete: (id: number) => api.delete(`/rules/${id}`),
};

// CSV Import
export const importAPI = {
  // Basic import (legacy)
  preview: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/preview', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  process: (file: File, mapping: any) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('mapping', JSON.stringify(mapping));
    return api.post('/import/process', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getMappings: () => api.get('/import/mappings'),
  saveMapping: (data: any) => api.post('/import/mappings', data),

  // Enhanced import (CSV, OFX, QFX with auto-detection)
  previewEnhanced: (file: File) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/import/preview-enhanced', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  processEnhanced: (file: File, options: {
    mapping: any;
    date_format?: string;
    account_id?: number;
    use_ai?: boolean;
    flag_duplicates?: boolean;
  }) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('options', JSON.stringify(options));
    return api.post('/import/process-enhanced', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  // Import batches
  getBatches: () => api.get('/import/batches'),
  deleteBatch: (batchId: number) => api.delete(`/import/batches/${batchId}`),
};

// History
export const historyAPI = {
  createSnapshot: () => api.post('/history/snapshot'),
};

export default api;
